import React, { useState } from 'react';
import type { Clinician } from '@features/organizations/types';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

interface OrgScheduleProviderFilterProps {
  clinicians: Clinician[];
  selectedProviderIds: string[];
  onSelectionChange: (ids: string[]) => void;
  isLoading: boolean;
}

export const OrgScheduleProviderFilter: React.FC<OrgScheduleProviderFilterProps> = ({
  clinicians,
  selectedProviderIds,
  onSelectionChange,
  isLoading,
}) => {
  const [expanded, setExpanded] = useState(true);
  const { orgSchedule } = useEventTracking();

  const handleToggle = (userId: string) => {
    const next = selectedProviderIds.includes(userId)
      ? selectedProviderIds.filter((id) => id !== userId)
      : [...selectedProviderIds, userId];
    onSelectionChange(next);
    orgSchedule.providerFilterChanged({
      selected_count: next.length,
      total_count: clinicians.length,
      action: 'individual_toggle',
    });
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: '15px', fontWeight: 400, color: '#535976' }}>
          Providers
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          aria-label={expanded ? 'Collapse providers' : 'Expand providers'}
        >
          {expanded ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <FormGroup>
            {clinicians.map((clinician) => (
              <FormControlLabel
                key={clinician.userId}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedProviderIds.includes(clinician.userId)}
                    onChange={() => handleToggle(clinician.userId)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px' }}>{clinician.name}</Typography>
                }
              />
            ))}
          </FormGroup>
        )}
      </Collapse>
    </Box>
  );
};
